import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Auth,
  FacebookAuthProvider,
  GoogleAuthProvider,
  User,
  UserCredential,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  linkWithPopup,
  unlink,
  AuthProvider,
  OAuthCredential,
  deleteUser,
} from '@angular/fire/auth';
import { get, getDatabase, ref, update } from '@angular/fire/database';
import { BehaviorSubject, Observable } from 'rxjs';

import { LoggerService } from './logger.service';
import { ProviderEnum } from '../models/preference';
import { CallResponseObject } from '../models/call';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private logger = inject(LoggerService);
  private auth = inject(Auth);
  public isAdmin: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _user: BehaviorSubject<User> = new BehaviorSubject(null);
  public readonly user: Observable<User> = this._user.asObservable();
  private _isLoggedIn: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public readonly isLoggedIn: Observable<boolean> = this._isLoggedIn.asObservable();

  constructor(public router: Router) {
    onAuthStateChanged(this.auth, user => {
      this._user.next(user);
      if (user !== null) {
        this._isLoggedIn.next(true);
        this.getIsAdmin()
          .then(result => this.isAdmin.next(result))
          .catch(err => this.logger.error('Auth:IsAd:Err:' + err));
      } else {
        this._isLoggedIn.next(false);
        this.isAdmin.next(false);
      }
    });
  }

  private getIsAdmin(): Promise<boolean> {
    const juizRef = ref(getDatabase(), 'juizes');

    return new Promise<boolean>((resolveAdmin, reject) => {
      get(juizRef)
        .then(snapshot => {
          if (snapshot.exists()) {
            this.logger.debug('Auth:IsAd:Ok');
            const juizes = snapshot.val();
            if (this.auth.currentUser.uid in juizes) {
              this.logger.debug('Auth:IsAd:True');
              resolveAdmin(true);
            } else {
              this.logger.debug('Auth:IsAd:False');
              resolveAdmin(false);
            }
          } else {
            this.logger.warning('Auth:IsAd:None');
            resolveAdmin(false);
          }
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  public doLogin(email: string, password: string): Promise<UserCredential> {
    return new Promise<UserCredential>((resolveEmailLogin, reject) => {
      signInWithEmailAndPassword(this.auth, email, password)
        .then(userCredential => {
          this.logger.debug('Auth:ELogin:Ok');
          resolveEmailLogin(userCredential);
        })
        .catch(err => {
          this.logger.error({ 'Auth:Login': err });
          reject(err);
        });
    });
  }

  public doRegister(email: string, password: string): Promise<UserCredential> {
    return new Promise<UserCredential>((resolveReg, reject) => {
      createUserWithEmailAndPassword(this.auth, email, password)
        .then(userCredential => {
          this.logger.debug('Auth:RegOk');
          resolveReg(userCredential);
        })
        .catch(err => {
          this.logger.error({ 'Auth:RegFailed': err });
          reject(err);
        });
    });
  }

  public sendEmailVerification(): void {
    sendEmailVerification(this.auth.currentUser)
      .then(() => {
        this.router.navigate(['perfil/verify-email']).catch(err => {
          this.logger.error({ 'Auth:VerEmailRoute': err });
        });
      })
      .catch(err => {
        this.logger.error({ 'Auth:VerEmail': err });
      });
  }

  public sendPasswordResetEmail(passwordResetEmail: string): Promise<void> {
    return sendPasswordResetEmail(this.auth, passwordResetEmail);
  }

  public logout(): Promise<void> {
    return signOut(this.auth)
      .then(() => {
        this.router.navigate(['/']).catch(err => {
          this.logger.error('Auth:LO:Route:' + err);
        });
      })
      .catch(err => {
        this.logger.error({ 'Auth:LO': err });
      });
  }

  public delete(): Promise<CallResponseObject> {
    return new Promise((resolve, reject) => {
      return deleteUser(this._user.value)
        .then(() => {
          this.logger.debug('Auth:Delete:Ok');
          this.logger.info('Bye, bye!!!');
          this.router.navigate(['/']).catch(err => {
            this.logger.error('Auth:Delete:Route:' + err);
            resolve({ status: 0, message: 'Uma pena vc nos deixar' });
          });
        })
        .catch(err => {
          this.logger.error({ 'Auth:Delete': err });
          if (err.code == 'auth/requires-recent-login') {
            resolve({
              status: 1,
              message: 'Para excluir o perfil é necessário estar logado há pouco tempo. Saia e entre novamente',
            });
          } else {
            reject(err);
          }
        });
    });
  }

  public doFacebookLogin(): Promise<UserCredential> {
    return new Promise<UserCredential>((resolveFLogin, reject) => {
      const provider = new FacebookAuthProvider();
      signInWithPopup(this.auth, provider).then(
        userCredential => {
          this.logger.debug({ 'Auth:FLogin:Ok:': userCredential });
          resolveFLogin(userCredential);
        },
        err => {
          this.logger.error({ 'Auth:FLogin': err });
          reject(err);
        }
      );
    });
  }

  public doGoogleLogin(): Promise<UserCredential> {
    return new Promise<UserCredential>((resolveGLogin, reject) => {
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      signInWithPopup(this.auth, provider).then(
        userCredential => {
          this.logger.debug({ 'Auth:GLogin:Ok:': userCredential });
          resolveGLogin(userCredential);
        },
        err => {
          this.logger.error({ 'Auth:GLogin': err });
          reject(err);
        }
      );
    });
  }

  public linkProvider(providerId: string): Promise<string> {
    let provider: AuthProvider;
    let providerName = '';

    return new Promise<string>((resolve, reject) => {
      if (providerId === ProviderEnum.Google) {
        provider = new GoogleAuthProvider();
        providerName = 'Google';
      } else if (providerId === ProviderEnum.Facebook) {
        provider = new FacebookAuthProvider();
        providerName = 'Facebook';
      } else {
        reject('Tipo de login não identificado. Tente novamente mais tarde');
      }

      return linkWithPopup(this._user.value, provider)
        .then(result => {
          let credential: OAuthCredential;
          if (providerId === ProviderEnum.Google) {
            credential = GoogleAuthProvider.credentialFromResult(result);
          } else {
            credential = FacebookAuthProvider.credentialFromResult(result);
          }
          this.logger.debug({ 'Auth:Link': credential });
          update(ref(getDatabase(), `usuarios/${this._user.value.uid}`), {
            providerGoogle: true,
          }).then(() => {
            resolve(`Conta do ${providerName} vinculada com sucesso`);
          });
        })
        .catch(err => {
          this.logger.error({ 'Auth:Link': err });
          if (err.code === 'auth/credential-already-in-use') {
            reject('Autenticação já utilizada em outra conta');
          } else if (err.code === 'auth/popup-closed-by-user') {
            reject('Janela fechada pelo usuário. Ação cancelada');
          } else {
            reject(`Falha ao vincular conta do ${providerName}`);
          }
        });
    });
  }

  public unlinkProvider(providerId: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      return unlink(this._user.value, providerId)
        .then(result => {
          this.logger.debug({ 'Auth:Unlink': result });
          const updateValue = {};
          if (providerId === ProviderEnum.Google) {
            updateValue['providerGoogle'] = false;
          } else if (providerId === ProviderEnum.Facebook) {
            updateValue['providerFacebook'] = false;
          } else {
            this.logger.error({ 'Auth:Unlink:NoProvider': providerId });
          }
          update(ref(getDatabase(), `usuarios/${this._user.value.uid}`), updateValue).then(() => {
            resolve('Conta desvinculada com sucesso');
          });
        })
        .catch(err => {
          this.logger.error({ 'Auth:Unlink': err });
          if (err.code === 'auth/credential-already-in-use') {
            reject('Autenticação já utilizada em outra conta');
          } else {
            reject('Falha ao vincular conta do Google');
          }
        });
    });
  }
}
